import { i18n } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";
import type { MessageReaction, ThreadMessage } from "@rakazo/contracts";
import type { FoldedTranscript, PeerExchangePair } from "@rakazo/core";
import {
  expandPeerReplies,
  groupVoiceChats,
  isPeerReceiptBlocks,
  isToolActivityBlock,
  projectMessageReactions,
  timeSeparatorIds,
  unreadDividerMessageId,
  voiceChatHoldsMessage,
} from "@rakazo/core";
import type { GroupAvatarMember } from "@rakazo/ui-web";
import { cn } from "@rakazo/ui-web";
import { ArrowDown } from "lucide-react";
import type { RefObject } from "react";
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ActiveBotGlyph } from "../../components/ai/CollaborationMarker";
import { VoiceChatCard } from "../../components/call/VoiceChatCard";
import { TerminalActivity } from "../../components/TerminalActivity";
import type { ArtifactTarget } from "../../lib/artifact-open";
import { quoteDraftForSelection } from "../../lib/quote-selection";
import { useTerminalPresentation } from "../../lib/terminal-mode";
import { messageHasVisibleBlocks } from "../../lib/tool-activity-view";
import { transcriptIsNearEnd, transcriptMovedDown } from "../../lib/transcript-scroll";
import { TimeSeparator, UnreadDivider } from "./chat-context";
import { MessageContextMenu } from "./message-context-menu";
import { MessageView } from "./message-view";
import { QuoteSelectionButton } from "./quote-selection-button";

export const Transcript = memo(function Transcript({
  scrollRef,
  scrollRequest,
  followLatestRequest,
  onScrollRequestHandled,
  artifactTarget,
  transcript,
  showToolActivity,
  olderCursor,
  unreadAfterSeq,
  loadingOlder,
  answerableAskIds,
  dismissableAskIds,
  running,
  workingBots,
  onCallBack,
  onLoadOlder,
  onOpenBot,
  onAnswer,
  onReply,
  onReference,
  onQuote,
  onReact,
  onJumpToMessage,
  onOpenPeerMessages,
  memberName,
  botName,
  peerBot,
  onRefresh,
  onBotChanged,
  onAddRoutine,
  voiceReady,
  speakingMessageId,
  onSpeak,
  onOpenComputer,
}: {
  scrollRef: RefObject<HTMLDivElement | null>;
  scrollRequest: { messageId: string; nonce: number } | null;
  followLatestRequest: number;
  onScrollRequestHandled: () => void;
  artifactTarget: ArtifactTarget;
  transcript: FoldedTranscript<ThreadMessage>;
  showToolActivity: boolean;
  olderCursor: number | null;
  /** The seq read up to when this chat opened; null when nothing was unread. */
  unreadAfterSeq: number | null;
  loadingOlder: boolean;
  answerableAskIds: Set<string>;
  dismissableAskIds: Set<string>;
  running: boolean;
  workingBots: GroupAvatarMember[];
  /** Calls the bot back from a missed call in the transcript. */
  onCallBack?: () => void;
  onLoadOlder: () => void | Promise<void>;
  onOpenBot: (botId: string) => void;
  onAnswer: (
    message: ThreadMessage,
    text: string,
    username?: string,
    dismiss?: boolean,
  ) => Promise<void>;
  onReference?: (message: ThreadMessage) => void;
  onReply: (message: ThreadMessage) => void;
  onQuote: (message: ThreadMessage, quote: string) => void;
  onReact: (message: ThreadMessage, reaction: MessageReaction) => Promise<void>;
  onJumpToMessage: (messageId: string) => void;
  onOpenPeerMessages: (peer: PeerExchangePair & { peerBotName?: string }) => void;
  botName?: string;
  memberName?: (botId: string | undefined) => string | undefined;
  peerBot: (botId: string) => { name?: string; color: string; status?: string } | undefined;
  onRefresh: () => Promise<void>;
  onBotChanged: () => Promise<void>;
  onAddRoutine: (name: string, prompt: string) => void;
  voiceReady: boolean;
  speakingMessageId: string | null;
  onSpeak: (message: ThreadMessage) => void;
  onOpenComputer: (botId?: string) => void;
}) {
  const { t } = useLingui();
  const terminal = useTerminalPresentation();
  const auditAnchors = new Map<string, string>();
  for (const message of transcript.messages) {
    if (
      message.runId &&
      message.blocks.some((b) => b.kind === "steps") &&
      !auditAnchors.has(message.runId)
    )
      auditAnchors.set(message.runId, message.id);
  }

  const [atEnd, setAtEnd] = useState(true);
  const following = useRef(true);
  const autoScrolling = useRef(false);
  const lastScrollTop = useRef<number | null>(null);
  const autoScrollTimer = useRef<number | undefined>(undefined);
  const jumpButtonRef = useRef<HTMLButtonElement>(null);
  const [expandedPeerMarkers, setExpandedPeerMarkers] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const togglePeerReplies = useCallback((markerId: string) => {
    // Like a pointer press: a keyboard toggle must not snap the transcript to its end.
    following.current = false;
    autoScrolling.current = false;
    setExpandedPeerMarkers((expanded) => {
      const next = new Set(expanded);
      if (!next.delete(markerId)) next.add(markerId);
      return next;
    });
  }, []);
  // A jump to a folded reply expands its marker; the scroll effect below then finds the row.
  useLayoutEffect(() => {
    const markerId = scrollRequest && transcript.markerOf.get(scrollRequest.messageId);
    if (!markerId) return;
    setExpandedPeerMarkers((expanded) =>
      expanded.has(markerId) ? expanded : new Set(expanded).add(markerId),
    );
  }, [scrollRequest, transcript]);
  const messages = useMemo(
    () => expandPeerReplies(transcript, expandedPeerMarkers),
    [transcript, expandedPeerMarkers],
  );
  const messageById = useMemo(
    () => new Map(messages.map((message) => [message.id, message])),
    [messages],
  );
  const reactionView = useMemo(() => projectMessageReactions(messages), [messages]);
  const shownMessages = reactionView.visibleMessages.filter((message) =>
    messageHasVisibleBlocks(message.blocks, showToolActivity),
  );
  const separatorIds = timeSeparatorIds(shownMessages);
  const unreadDividerId = unreadDividerMessageId(shownMessages, unreadAfterSeq, olderCursor);
  const workingBotName = workingBots.length === 1 ? workingBots[0]?.name : undefined;
  const workingLabel =
    workingBotName != null && workingBotName !== ""
      ? t`${workingBotName} is working`
      : t`Bots are working`;
  const [quoteDraft, setQuoteDraft] = useState<{
    message: ThreadMessage;
    text: string;
    range: Range;
  } | null>(null);
  const selectingWithMouse = useRef(false);

  const evaluateSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      setQuoteDraft(null);
      return;
    }
    const range = selection.getRangeAt(0);
    const contentOf = (node: Node) =>
      (node instanceof Element ? node : node.parentElement)?.closest<HTMLElement>(
        "[data-quote-message-id]",
      ) ?? null;
    const startContent = contentOf(range.startContainer);
    const endContent = contentOf(range.endContainer);
    // selection.toString() serializes the whole range (a Ctrl+A transcript is
    // unbounded), so it only runs once both endpoints sit in one message.
    const draft = quoteDraftForSelection(
      {
        startContent,
        endContent,
        text: startContent && startContent === endContent ? selection.toString() : "",
      },
      messageById,
    );
    setQuoteDraft((prev) => {
      if (!draft) return null;
      // Repeat firings for an unchanged selection reuse the draft so the
      // transcript isn't re-rendered by every unrelated selection event. A
      // moved selection (e.g. keyboard-selecting a second occurrence of the
      // same text) must carry its new Range — the pill anchors to it.
      if (
        prev &&
        prev.message === draft.message &&
        prev.text === draft.text &&
        prev.range.compareBoundaryPoints(Range.START_TO_START, range) === 0 &&
        prev.range.compareBoundaryPoints(Range.END_TO_END, range) === 0
      ) {
        return prev;
      }
      return { ...draft, range };
    });
  }, [messageById]);

  // Keyboard and assistive-tech selections never reach a mouseup, so the pill
  // lifecycle listens on selectionchange; the mouse flag keeps it hidden while
  // a drag is still in flight.
  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      selectingWithMouse.current = true;
      if ((event.target as Element | null)?.closest?.("[data-quote-selection]")) return;
      setQuoteDraft(null);
    };
    const onMouseUp = () => {
      selectingWithMouse.current = false;
      evaluateSelection();
    };
    const onSelectionChange = () => {
      if (!selectingWithMouse.current) evaluateSelection();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setQuoteDraft(null);
    };
    // A drag that ends outside the window never fires document mouseup; reset
    // the flag on blur so keyboard selections keep working afterwards.
    const onWindowBlur = () => {
      selectingWithMouse.current = false;
    };
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("mouseup", onMouseUp, true);
    document.addEventListener("selectionchange", onSelectionChange);
    document.addEventListener("keydown", onKey);
    window.addEventListener("blur", onWindowBlur);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("mouseup", onMouseUp, true);
      document.removeEventListener("selectionchange", onSelectionChange);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, [evaluateSelection]);
  const snapToEnd = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    following.current = true;
    autoScrolling.current = false;
    setAtEnd(true);
    element.scrollTo({ top: element.scrollHeight, behavior: "auto" });
  }, [scrollRef]);

  const viewportSize = useRef({ width: 0, height: 0 });
  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const observer = new ResizeObserver(() => {
      viewportSize.current = { width: element.clientWidth, height: element.clientHeight };
      if (following.current) snapToEnd();
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [scrollRef, snapToEnd]);

  const jumpToLatest = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    following.current = true;
    autoScrolling.current = !reducedMotion;
    setAtEnd(true);
    element.scrollTo({
      top: element.scrollHeight,
      behavior: reducedMotion ? "auto" : "smooth",
    });
    window.clearTimeout(autoScrollTimer.current);
    // Fallback only: onScroll clears autoScrolling once near-end is reached.
    autoScrollTimer.current = window.setTimeout(
      () => {
        autoScrolling.current = false;
      },
      reducedMotion ? 0 : 2_000,
    );
  }, [scrollRef]);

  const scrolledJump = useRef<number | null>(null);
  const jumpScrolling = useRef(false);
  const jumpScrollTimer = useRef<number | undefined>(undefined);
  const endJumpScroll = useCallback(() => {
    jumpScrolling.current = false;
    window.clearTimeout(jumpScrollTimer.current);
    scrollRef.current?.removeEventListener("scrollend", endJumpScroll);
  }, [scrollRef]);
  // A jump scroll must win over follow-the-tail: unfollow inside the commit
  // that mounts the row so a live commit cannot cancel the animation, keep
  // retrying while the pinned window is still rendering, and suppress the
  // near-end follow re-arm only for the jump's own scroll events — scrollend
  // (or user input interrupting it, which also fires scrollend) ends the
  // suppression, with the timeout as fallback when no scroll happens.
  useLayoutEffect(() => {
    if (!scrollRequest || scrolledJump.current === scrollRequest.nonce) return;
    const element = scrollRef.current;
    const row = element?.querySelector(
      `[data-message-id="${CSS.escape(scrollRequest.messageId)}"]`,
    );
    if (!element || !row) return;
    scrolledJump.current = scrollRequest.nonce;
    following.current = false;
    autoScrolling.current = false;
    jumpScrolling.current = true;
    element.addEventListener("scrollend", endJumpScroll, { once: true });
    window.clearTimeout(jumpScrollTimer.current);
    jumpScrollTimer.current = window.setTimeout(endJumpScroll, 2_000);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Streaming layout updates can interrupt a smooth jump before it reaches the row.
    row.scrollIntoView({ behavior: "auto", block: "center" });
    row.animate(
      reducedMotion
        ? [{ backgroundColor: "var(--muted)" }, { backgroundColor: "var(--muted)" }]
        : [
            { backgroundColor: "transparent" },
            { backgroundColor: "var(--muted)" },
            { backgroundColor: "transparent" },
          ],
      { duration: 1200 },
    );
    onScrollRequestHandled();
  }, [messages, scrollRequest, scrollRef, endJumpScroll, onScrollRequestHandled]);

  const followedRequest = useRef(followLatestRequest);
  useLayoutEffect(() => {
    if (followedRequest.current === followLatestRequest) return;
    followedRequest.current = followLatestRequest;
    endJumpScroll();
    snapToEnd();
  }, [followLatestRequest, endJumpScroll, snapToEnd]);

  useLayoutEffect(() => {
    if (following.current) snapToEnd();
  }, [messages, running, snapToEnd]);

  useLayoutEffect(() => {
    const button = jumpButtonRef.current;
    if (atEnd && button && document.activeElement === button) {
      button.blur();
    }
  }, [atEnd]);

  const loadOlder = useCallback(() => {
    const wasFollowing = following.current;
    // Prepend must not race the messages-driven snap-to-end follow path.
    following.current = false;
    autoScrolling.current = false;
    const pending = onLoadOlder();
    if (!pending) return;
    return Promise.resolve(pending).catch((error) => {
      const element = scrollRef.current;
      if (wasFollowing && element && transcriptIsNearEnd(element)) {
        following.current = true;
        setAtEnd(true);
      }
      throw error;
    });
  }, [onLoadOlder, scrollRef]);

  useEffect(
    () => () => {
      window.clearTimeout(autoScrollTimer.current);
      window.clearTimeout(jumpScrollTimer.current);
    },
    [],
  );

  useEffect(() => {
    const node = scrollRef.current;
    if (!node || terminal) return;
    let timer: ReturnType<typeof setTimeout>;
    const wheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || event.ctrlKey) return;
      let target = event.target instanceof HTMLElement ? event.target : null;
      while (target && target !== node) {
        if (
          target.scrollWidth > target.clientWidth &&
          ["auto", "scroll"].includes(getComputedStyle(target).overflowX)
        )
          return;
        target = target.parentElement;
      }
      event.preventDefault();
      node.scrollLeft = 0;
      node.toggleAttribute("data-reveal-times", event.deltaX > 0);
      clearTimeout(timer);
      timer = setTimeout(() => node.removeAttribute("data-reveal-times"), 1000);
    };
    node.addEventListener("wheel", wheel, { passive: false });
    return () => {
      node.removeEventListener("wheel", wheel);
      clearTimeout(timer);
      node.removeAttribute("data-reveal-times");
    };
  }, [scrollRef, terminal]);

  return (
    <div className="chat-transcript-frame relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
      <div
        ref={scrollRef}
        data-testid="transcript"
        onPointerDown={(event) => {
          lastScrollTop.current = event.currentTarget.scrollTop;
          autoScrolling.current = false;
          following.current = false;
        }}
        onTouchStart={(event) => {
          lastScrollTop.current = event.currentTarget.scrollTop;
          autoScrolling.current = false;
          following.current = false;
        }}
        onWheel={(event) => {
          if (event.deltaY < 0) {
            lastScrollTop.current = event.currentTarget.scrollTop;
            autoScrolling.current = false;
            following.current = false;
          }
        }}
        onScroll={(event) => {
          // Reflow can emit scroll before ResizeObserver. It is not a user
          // scroll-away, and must not disable following the latest message.
          const viewport = viewportSize.current;
          if (
            viewport.width !== event.currentTarget.clientWidth ||
            viewport.height !== event.currentTarget.clientHeight
          )
            return;
          const scrolledDown = transcriptMovedDown(
            lastScrollTop.current,
            event.currentTarget.scrollTop,
          );
          lastScrollTop.current = event.currentTarget.scrollTop;
          const nearEnd = transcriptIsNearEnd(event.currentTarget);
          setAtEnd(nearEnd);
          // A jump scroll owns the viewport until its animation settles; its
          // own near-end crossings must not re-arm tail-following.
          if (jumpScrolling.current) return;
          if (nearEnd) {
            if (scrolledDown) following.current = true;
            if (autoScrolling.current) {
              autoScrolling.current = false;
              window.clearTimeout(autoScrollTimer.current);
            }
          } else if (!autoScrolling.current) {
            following.current = false;
          }
        }}
        data-terminal-transcript={terminal || undefined}
        className="rk-scroll flex min-h-0 flex-1 flex-col gap-2 overflow-x-hidden overscroll-x-none overflow-y-auto px-4 py-4 md:px-6 md:py-4"
      >
        {olderCursor != null ? (
          <button
            type="button"
            disabled={loadingOlder}
            onClick={() => void loadOlder()}
            className="self-center rounded-lg px-3 py-1.5 text-[13px] text-muted-foreground hover:bg-muted hover:text-foreground/75 disabled:opacity-50"
          >
            {loadingOlder ? t`Loading…` : t`Load earlier messages`}
          </button>
        ) : null}
        {groupVoiceChats(reactionView.visibleMessages).map((item) => {
          if (item.kind === "voiceChat") {
            return (
              <div key={item.key}>
                {item.messages[0] && separatorIds.has(item.messages[0].id) ? (
                  <TimeSeparator
                    createdAt={item.messages[0].createdAt}
                    locale={i18n.locale || "en"}
                  />
                ) : null}
                {voiceChatHoldsMessage(item, unreadDividerId) ? (
                  <UnreadDivider
                    afterSeparator={Boolean(
                      item.messages[0] && separatorIds.has(item.messages[0].id),
                    )}
                  />
                ) : null}
                <VoiceChatCard
                  group={item}
                  revealMessageId={scrollRequest?.messageId}
                  onCallBack={onCallBack}
                />
              </div>
            );
          }
          const message = item.message;
          if (
            message.blocks.every((block) => block.kind === "task") &&
            message.task &&
            scrollRequest?.messageId !== message.id &&
            messages.some(
              (other) =>
                other.id !== message.id &&
                other.runId === message.runId &&
                other.role === "bot" &&
                other.blocks.some(
                  (block) =>
                    block.kind === "text" ||
                    block.kind === "ask" ||
                    block.kind === "image" ||
                    block.kind === "file",
                ),
            )
          )
            return null;
          const audit =
            terminal &&
            message.runId &&
            message.botId &&
            auditAnchors.get(message.runId) === message.id;
          if (
            !messageHasVisibleBlocks(message.blocks, terminal ? false : showToolActivity) &&
            !audit
          )
            return null;
          const peerReceipt = isPeerReceiptBlocks(message.blocks);
          const messageReactions = reactionView.reactions.get(message.id);
          return (
            <Fragment key={message.id}>
              {separatorIds.has(message.id) ? (
                <TimeSeparator createdAt={message.createdAt} locale={i18n.locale || "en"} />
              ) : null}
              {message.id === unreadDividerId ? (
                <UnreadDivider afterSeparator={separatorIds.has(message.id)} />
              ) : null}
              <MessageContextMenu
                onReference={onReference}
                message={message}
                onReply={onReply}
                onReact={onReact}
              >
                <div
                  data-message-id={message.id}
                  data-message-role={message.role}
                  data-peer-receipt={peerReceipt || undefined}
                  className={peerReceipt ? "relative py-0.5" : "group/message relative hover:z-20"}
                >
                  {terminal &&
                  !peerReceipt &&
                  message.blocks.some((b) => b.kind === "text" || b.kind === "progress") ? (
                    <div
                      data-terminal-message-meta=""
                      className="mb-2 flex items-center justify-between text-xs text-muted-foreground"
                    >
                      <span>
                        {message.role === "user"
                          ? `❯ ${t`You`}`
                          : (memberName?.(message.botId) ?? botName ?? t`Assistant`)}
                      </span>
                      <time dateTime={message.createdAt}>
                        {new Date(message.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </time>
                    </div>
                  ) : null}
                  <div
                    className={
                      peerReceipt
                        ? undefined
                        : `relative flex ${message.role === "user" ? "justify-end" : "justify-start"}`
                    }
                  >
                    <div
                      data-testid={peerReceipt ? undefined : "message-bubble-frame"}
                      className={
                        peerReceipt
                          ? undefined
                          : message.blocks.some(
                                (block) =>
                                  block.kind === "ask" ||
                                  block.kind === "mcp_approval" ||
                                  block.kind === "choice",
                              )
                            ? "relative w-full min-w-0 max-w-full"
                            : `relative ${message.blocks.some((block) => block.kind === "app_connect") ? "w-full" : "w-fit"} min-w-0 ${
                                message.role === "user"
                                  ? "max-w-[min(84%,calc(100%_-_8rem))] [@media(hover:none)]:max-w-[84%]"
                                  : "max-w-[min(88%,calc(100%_-_8rem))] [@media(hover:none)]:max-w-[88%]"
                              }`
                      }
                    >
                      <MessageView
                        artifactTarget={artifactTarget}
                        message={message}
                        canAnswer={answerableAskIds.has(message.id)}
                        canDismiss={dismissableAskIds.has(message.id)}
                        onOpenBot={onOpenBot}
                        onOpenPeerMessages={onOpenPeerMessages}
                        peerReplies={
                          transcript.folded.has(message.id)
                            ? expandedPeerMarkers.has(message.id)
                              ? "expanded"
                              : "collapsed"
                            : undefined
                        }
                        onTogglePeerReplies={togglePeerReplies}
                        onAnswer={onAnswer}
                        speakerName={
                          terminal || peerReceipt
                            ? undefined
                            : message.role === "bot"
                              ? memberName?.(message.botId)
                              : undefined
                        }
                        memberName={memberName}
                        peerBot={peerBot}
                        replyParent={
                          message.replyToMessageId
                            ? messageById.get(message.replyToMessageId)
                            : undefined
                        }
                        onJumpToMessage={onJumpToMessage}
                        onRefresh={onRefresh}
                        onBotChanged={onBotChanged}
                        onAddRoutine={onAddRoutine}
                        voiceReady={voiceReady}
                        speaking={speakingMessageId === message.id}
                        onSpeak={onSpeak}
                        onOpenComputer={onOpenComputer}
                        showToolActivity={terminal ? false : showToolActivity}
                        onReply={onReply}
                        onReference={onReference}
                        onReact={onReact}
                      />
                    </div>
                  </div>
                  {!terminal && !peerReceipt ? (
                    <time className="message-swipe-time" dateTime={message.createdAt}>
                      {new Date(message.createdAt).toLocaleTimeString([], {
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </time>
                  ) : null}
                  {audit ? (
                    <TerminalActivity
                      botId={message.botId!}
                      runId={message.runId!}
                      live={running && message.id.startsWith("progress:")}
                    />
                  ) : null}
                  {!peerReceipt && messageReactions ? (
                    <div
                      data-testid="message-reactions"
                      className={cn(
                        "mt-1 flex flex-wrap gap-1",
                        message.role === "user" && "justify-end",
                      )}
                    >
                      {[...messageReactions].map(([emoji, count]) => (
                        <span
                          key={emoji}
                          className="rounded-full border border-border bg-muted px-2 py-0.5 text-xs"
                        >
                          {emoji}
                          {count > 1 ? ` ${count}` : ""}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </MessageContextMenu>
            </Fragment>
          );
        })}
        {running &&
        !messages.some(
          (message) =>
            message.id.startsWith("progress:") &&
            message.blocks.some(
              (block) =>
                block.kind === "progress" && !isToolActivityBlock(block) && Boolean(block.text),
            ),
        ) ? (
          <ActiveBotGlyph bots={workingBots} label={workingLabel} />
        ) : null}
      </div>
      {quoteDraft ? (
        <QuoteSelectionButton
          range={quoteDraft.range}
          boundary={scrollRef.current}
          onQuote={() => {
            onQuote(quoteDraft.message, quoteDraft.text);
            window.getSelection()?.removeAllRanges();
            setQuoteDraft(null);
          }}
        />
      ) : null}
      <button
        ref={jumpButtonRef}
        type="button"
        aria-label={t`Jump to latest`}
        aria-hidden={atEnd}
        tabIndex={atEnd ? -1 : 0}
        onClick={jumpToLatest}
        className={`absolute bottom-4 left-1/2 z-20 grid h-9 w-9 -translate-x-1/2 place-items-center rounded-full border border-border bg-muted/95 text-foreground/75 shadow-md backdrop-blur transition-[opacity,transform,background-color] duration-200 ease-[cubic-bezier(.22,1,.36,1)] hover:bg-border motion-reduce:transition-none ${
          atEnd ? "pointer-events-none translate-y-2 opacity-0" : "translate-y-0 opacity-100"
        }`}
      >
        <ArrowDown size={17} strokeWidth={1.8} />
      </button>
    </div>
  );
});

/**
 * Floating Quote action anchored to the selection's bounding rect. Measures
 * itself after mount so it can flip below the selection when there is no room
 * above and stay clamped inside the viewport; re-anchors on scroll/resize.
 */
